import { SentryErrorBoundary } from "@/lib/observability/sentry";
import { reportError } from "@/lib/reportError";
import { handleStaleChunkError, isStaleChunkError, STALE_CHUNK_RELOAD_KEY } from "@/lib/chunkError";

function Fallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  console.error('[AppErrorBoundary]', error);

  // Silently reload for stale-chunk errors so users never see the error UI.
  // After a new Vite deploy, chunk filenames change. Any user with the old
  // HTML in memory will 404 on the old chunk URL. A hard reload fetches fresh
  // HTML with the new hashes. The sessionStorage guard in handleStaleChunkError
  // prevents an infinite reload loop on genuinely broken deploys.
  if (handleStaleChunkError(error)) {
    return null;
  }

  // Clear the reload guard so a subsequent page load can retry once if needed.
  sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-background">
      <h1 className="font-serif text-2xl font-bold text-foreground">Something went wrong.</h1>
      <p className="max-w-lg text-center text-sm text-muted-foreground">
        We've been notified and are looking into it. You can try refreshing the page or returning to the dashboard.
      </p>
      {import.meta.env.DEV && (
        <pre className="max-w-2xl overflow-auto rounded border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { resetError(); }}
          className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
        >
          Try again
        </button>
        <a
          href="/"
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
        >
          Back to home
        </a>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <SentryErrorBoundary
      fallback={(props) => <Fallback {...props} />}
      onError={(error) => { if (!isStaleChunkError(error)) reportError(error); }}
    >
      {children}
    </SentryErrorBoundary>
  );
}
